<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import UnitLinkCard from '@/components/cards/UnitLinkCard.vue';
import Pagination from '@/components/utilities/Pagination.vue';
import { useUnitsApi } from '@/composables/useUnitsApi';
import { useRegionsApi } from '@/composables/useRegionsApi';
import { useBuildingFacadesApi } from '@/composables/useBuildingFacadesApi';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { usePurchaseFilters } from '@/composables/usePurchaseFilters';
import { useRentalFilters } from '@/composables/useRentalFilters';
import type { LocaleSchema } from '@/locales/index';
import type { Unit } from '@/typings/units/Unit';
import type { Region } from '@/typings/regions/Region';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { GetUnitsRequest } from '@/typings/requests/GetUnitsRequest';
import { DEFAULT_RENTAL_UNIT_STATUSES, DEFAULT_RENTAL_VALID_UNIT_STATUSES } from '@/constants/rentalConstants';
import { DEFAULT_PURCHASE_UNIT_STATUSES, DEFAULT_PURCHASE_VALID_UNIT_STATUSES } from '@/constants/purchaseConstants';
import { ListingTypeEnum } from '@/enums/ListingType';
import { InlineSearchBar } from '@/components/utilities/search';
import { useUnit } from '@/composables/useUnit';
import { listingTypeEnumNumericValue } from '@/enums/ListingType';
import { UnitStatusEnum, isUnitDisabled } from '@/enums/UnitStatus';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const props = defineProps<{
  listingType: ListingTypeEnum
}>();

const { listUnitsPaginated, listUnitsLoading } = useUnitsApi();
const { sortUnitsByStatus } = useUnit();
const { getAllRegions } = useRegionsApi();
const { getBuildingFacades } = useBuildingFacadesApi();
const { getRentalUnitWizardUrl, rentUrl } = useRentalLocalizedUrls();
const { getPurchaseUnitWizardUrl, buyUrl } = usePurchaseLocalizedUrls();
const { loadFiltersFromRentalStorage, countSecondaryRentalFilters: _countSecondaryFilters } = useRentalFilters();
const { loadFiltersFromPurchaseStorage, countSecondaryPurchaseFilters: _countSecondaryPurchaseFilters } = usePurchaseFilters();

// État des régions
const regions = ref<Region[]>([]);

// État des façades de bâtiment
const buildingFacades = ref<BuildingFacade[]>([]);

// État des unités et pagination
const units = ref<Unit[]>([]);
const totalCount = ref(0);
const currentPage = ref(1);
const pageSize = ref(20);
const totalPages = ref(0);

interface FilterData {
  regionId: number | null
  availabilityDates: string[]
  unitConfiguration: string[]
  floors: number[]
  minPrice: number | undefined
  maxPrice: number | undefined
}

interface RentalFilterData extends FilterData {
  dogAllowed: boolean | undefined
}

const currentFilters = ref<FilterData | RentalFilterData>({
  regionId: null,
  availabilityDates: [],
  unitConfiguration: [],
  dogAllowed: undefined,
  floors: [],
  minPrice: undefined,
  maxPrice: undefined
});

const secondaryFiltersCount = ref(0);

const isLoading = ref(false);

const getUnitWizardUrlWithReturnUrl = (unit: Unit) => {
  const unitUrl = props.listingType === ListingTypeEnum.Rental ? getRentalUnitWizardUrl(unit) : getPurchaseUnitWizardUrl(unit);
  return `${unitUrl}?returnUrl=${encodeURIComponent(window.location.href)}`;
};

const headerTitle = computed(() => {
  return t('browsingApp.units.searchResults', {
    count: totalCount.value || units.value.length
  });
});

const onPageChanged = (page: number) => {
  currentPage.value = page;
  loadUnitsWithFilters();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Navigation
function onBackToHome() {
  window.location.href = props.listingType === ListingTypeEnum.Sale ? buyUrl : rentUrl;
}

function onUnitSelected(unit: Unit) {
  // Navigation vers la fiche d'unité
  window.location.href = getUnitWizardUrlWithReturnUrl(unit);
}

// Chargement des unités avec filtres et pagination
const loadUnitsWithFilters = async () => {
  try {
    const request: GetUnitsRequest = {
      listingTypes: [props.listingType],
      locale: locale.value.substring(0, 2),
      statuses: [UnitStatusEnum.Available]
    };

    if (currentFilters.value.regionId != null) {
      request.regionIds = [currentFilters.value.regionId];
    }

    if (currentFilters.value.unitConfiguration != null && currentFilters.value.unitConfiguration.length > 0) {
      request.unitConfigurations = currentFilters.value.unitConfiguration;
    }

    if (currentFilters.value.availabilityDates != null && currentFilters.value.availabilityDates.length > 0) {
      request.availabilityDates = currentFilters.value.availabilityDates;
    }

    if (currentFilters.value.floors != null && currentFilters.value.floors.length > 0) {
      request.floorNumbers = currentFilters.value.floors;
    }

    if (currentFilters.value.minPrice != null) {
      request.minPrice = currentFilters.value.minPrice;
    }

    if (currentFilters.value.maxPrice != null) {
      request.maxPrice = currentFilters.value.maxPrice;
    }

    if (props.listingType === ListingTypeEnum.Rental && (currentFilters.value as RentalFilterData).dogAllowed != null) {
      request.dogAllowed = (currentFilters.value as RentalFilterData).dogAllowed;
    }    
    
    request.page = currentPage.value;
    request.pageSize = pageSize.value;
    
    const response = await listUnitsPaginated(request as GetUnitsRequest & { page: number; pageSize: number });
    units.value = response.units;
    totalCount.value = response.totalCount;
    totalPages.value = response.totalPages;
    
    // Charger les façades de bâtiment pour les unités chargées
    if (units.value.length > 0) {
      const uniqueBuildingIds = [...new Set(units.value.map(unit => unit.buildingId))];
      const allFacades: BuildingFacade[] = [];
      
      for (const buildingId of uniqueBuildingIds) {
        try {
          const facades = await getBuildingFacades(buildingId);
          allFacades.push(...facades);
        } catch (error) {
          console.error(`Failed to load facades for building ${buildingId}:`, error);
        }
      }
      
      buildingFacades.value = allFacades;
    }
    
  } catch (error) {
    console.error('Failed to load units:', error);
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const loadCurrentFiltersFromUrl = (params: URLSearchParams) => {
  currentFilters.value = {
    regionId: params.get('regionIds') ? Number(params.get('regionIds')) : null,
    availabilityDates: params.get('availabilityDates') ? params.get('availabilityDates')!.split(';') : [],
    unitConfiguration: params.get('unitConfigurations') ? params.get('unitConfigurations')!.split(';') : [],
    floors: params.get('floors') ? params.get('floors')!.split(';').map(f => Number(f)) : [],
    minPrice: params.get('minPrice') ? Number(params.get('minPrice')) : undefined,
    maxPrice: params.get('maxPrice') ? Number(params.get('maxPrice')) : undefined
  };
  if (props.listingType === ListingTypeEnum.Rental) {
    (currentFilters.value as RentalFilterData).dogAllowed = params.get('dogAllowed') ? params.get('dogAllowed') === '1' : undefined;
  }

  // Calculer le nombre de filtres secondaires
  secondaryFiltersCount.value = 0;
  if (currentFilters.value.floors.length > 0) {
    secondaryFiltersCount.value++;
  }
  if (currentFilters.value.minPrice !== undefined || currentFilters.value.maxPrice !== undefined) {
    secondaryFiltersCount.value++;
  }
};

// Appliquer les filtres aux unités
const applyFilters = () => {
  const params = new URLSearchParams(window.location.search);
  const hasParams = params.get('regionIds') || params.get('availabilityDates') ||
                    params.get('unitConfigurations') || params.get('dogAllowed') ||
                    params.get('floors') || params.get('minPrice') || params.get('maxPrice');
  if (hasParams) {
    loadCurrentFiltersFromUrl(params);
  }
  currentPage.value = 1;
  loadUnitsWithFilters();
};

onMounted(async () => {
  listUnitsLoading.value = true;
  // Charger seulement les régions
  try {
    const regionsResponse = await getAllRegions({
      locale: locale.value.substring(0, 2),
      unitStatuses: props.listingType === ListingTypeEnum.Sale ? DEFAULT_PURCHASE_VALID_UNIT_STATUSES : DEFAULT_RENTAL_VALID_UNIT_STATUSES,
      listingType: listingTypeEnumNumericValue[props.listingType]
    });
    regions.value = regionsResponse;
  } catch (error) {
    console.error('Failed to load data:', error);
  }
  applyFilters();
  listUnitsLoading.value = false;
});
</script>

<template>
  <!-- Recherche d'unités de location -->
  <div class="rental-units-search">
    <!-- Barre de filtres -->
    <InlineSearchBar action-type="filter"
                     :units-search="true"
                     :listing-type="props.listingType"
                     :filters-count="secondaryFiltersCount"
                     @filters-applied="applyFilters" />
    <div class="wrapper wrapper_large">      
      <!-- Contenu principal -->
      <div class="main-content">
        <!-- En-tête avec bouton de retour -->
        <HeaderWithBackButton :title="headerTitle"
                              @back-click="onBackToHome" />
        
        <!-- Liste des unités -->
        <div v-if="listUnitsLoading"
             class="browsingApp-loadingState">
          <LoadingSpinner />
        </div>

        <template v-else-if="units?.length">
          <div class="showPage"> {{ t('pagination.showing', { page: currentPage, totalPages: totalPages }) }}</div>
          <div class="units-grid">
            <UnitLinkCard v-for="unit in sortUnitsByStatus(units)"
                          :key="unit.id"
                          :unit="unit"
                          :href="!isUnitDisabled(unit.status) ? getUnitWizardUrlWithReturnUrl(unit) : ''"
                          :unitsSearch="true"
                          @click="onUnitSelected"
                          class="unitLinkList-unitCard"
                          :building-facades="buildingFacades" />
          </div>

          <!-- Composant Pagination -->
          <Pagination v-if="totalCount > pageSize"
                         :current-page="currentPage"
                         :total-items="totalCount"
                         :items-per-page="pageSize"
                         @page-changed="onPageChanged" />
        </template>
        
        <div v-else-if="!listUnitsLoading"
             class="placeholderNoInformation">
          {{ t('browsingApp.units.noUnitsAvailable') }}
        </div>
      </div>
    </div>
  </div>
</template>

